/**
 * FloatingHomeBtn — small gold-on-charcoal pill with crossed swords, fixed to the
 * bottom-right of the viewport. Rendered globally at the App level so every route
 * except the landing page ("/") and auth callbacks get a one-tap shortcut back
 * to the homepage.
 *
 * Owen's brief (Feb 2026): the crossed swords are the platform's universal
 * "exit the arena / return home" gesture. Sitting in the bottom-right where
 * the thumb naturally rests on mobile, and visible always like a task-bar
 * element on laptop. Touch target sized 44×44 on mobile (Apple HIG minimum).
 *
 * Owen Jun 2026 round 22 — also hidden on the cinematic /tour and the
 * public /tribute/* views. Those screens host the prominent gold Spotify
 * card centred at the bottom, and Spotify's own Play button lives on the
 * RIGHT edge of the track embed. The crossed-swords pill was physically
 * landing on top of that Play button on mobile, blocking the tap.
 *
 * Owen Feb 2026 (later) — routing is now auth-aware. Signed-in users land
 * on their /dashboard (their real "home" inside the product); signed-out
 * visitors land on the public / (marketing landing). Previously EVERY
 * click landed on / regardless of state, which meant a signed-in user
 * momentarily saw the public "Join / Battle On" CTAs on the ~500ms flash
 * before Landing.js resolved /auth/me — reading like an accidental
 * sign-out. No auth state is ever changed; only the destination.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swords } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const HIDE_ON_PATHS = new Set(['/', '/auth/callback']);
const HIDE_ON_PREFIXES = ['/tour', '/tribute/'];

const FloatingHomeBtn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  // Owen Feb 28, 2026 round 64 — also auto-hide while any modal/dialog
  // is open so the swords don't sit on top of modal action bars.
  // Mirrors the same logic in FloatingUtilities.jsx.
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      const candidates = document.querySelectorAll(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-testid$="-modal"], [data-testid="selective-mirror-modal"]'
      );
      setModalOpen(candidates.length > 0);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => observer.disconnect();
  }, []);

  // Hide on the landing page itself (already home), the OAuth callback,
  // and any cinematic / tribute view where the bottom strip is owned by
  // the Spotify Play button.
  if (HIDE_ON_PATHS.has(location.pathname)) return null;
  if (HIDE_ON_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;
  if (modalOpen) return null;

  return (
    <button
      type="button"
      onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
      data-testid="floating-home-btn"
      aria-label={isAuthenticated ? 'Back to the Colosseum — Dashboard' : 'Back to the Colosseum'}
      title={isAuthenticated ? 'Back to the Colosseum' : 'Back to the Colosseum landing'}
      className="theme-fixed fixed bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] right-4 sm:bottom-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] sm:right-[calc(env(safe-area-inset-right,0px)+1.25rem)] z-[60] h-10 pl-2.5 pr-3.5 rounded-full bg-[#2A2522]/95 backdrop-blur-sm border border-[#D4AF37] text-[#D4AF37] inline-flex items-center gap-2 shadow-[0_3px_10px_rgba(0,0,0,0.5)] hover:bg-[#1C1917] hover:text-[#E8C5A0] hover:shadow-[0_4px_14px_rgba(212,175,55,0.4)] transition-all"
    >
      {/* Owen Feb 2026 — turned the icon-only swords pill into a labeled
          "Back" pill after Owen's feedback that a first-time reader
          returning from a Bookshop.org / Discogs tab couldn't tell the
          gold swords glyph was the way home. Same aesthetic family
          (charcoal + gold border + swords), just with a short "Back"
          word so the affordance reads at a glance. Kept small enough
          (h-10) that it never competes with page content. */}
      <Swords className="w-[16px] h-[16px]" />
      <span
        className="text-[11px] uppercase tracking-[0.22em] font-medium"
        style={{ fontFamily: 'Work Sans, sans-serif' }}
      >
        {t('common.back')}
      </span>
    </button>
  );
};

export default FloatingHomeBtn;
